/**
 * 수정자 : 박승호
 * 날짜 : 2025.04.04
 * 기능 : 실시간 방송 모달 [ 실시간 음성 방송, 실시간 음원 방송, 실시간 TTS 방송 ]
 */

import { useState, Dispatch, SetStateAction, useEffect, useRef, ChangeEvent } from 'react';
import { IModal } from 'customTypes';
import Portal from '../Portal';
import DeleteModal from './DeleteModal2';
import { useTranslation } from 'react-i18next';
import { BtnBlue, BtnDeep, BtnGhost, BtnRed } from '../Button';
import WebSdkModal from './WebSdkModal';
import { WebSDKspeaker } from '../../utils/webSDKspeakerClass';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import BroadcastEquipList from '../BroadcastEquipList';
import {
  createChimeAudio,
  endAudio,
  END_CHAIM_AUDIO,
  getBroadcastingSpeakers,
  getConnectedSpeakers,
  hasAvailableSpeaker,
  hasConnectingSpeaker,
  isInputEmpty,
  playTTS,
  START_CHAIM_AUDIO,
  stopAudio,
  stopSpeakers,
} from '../../services/Tunnel';
import AccordionTextArea from '../AccordionTextArea';
import i18n from '../../translation/i18n';
import BroadcastLanguage from '../BroadcastLanguage';
import ModalBottomButton from '../button/ModalBottomButton';
import CheckChipButton from '../button/CheckChipButton';
import TextInfoBox from '../TextInfoBox';
import { apiGet, apiPatch } from '../../services/_common';
import { useRecoilValue } from 'recoil';
import { userState } from '../../atoms';
import { useQuery } from '@tanstack/react-query';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
import { isMobile } from '../../utils/checkMobile';
import { useSetAuth } from '../../utils/useSetAuth';
import IssueGuide from '../IssueGuide';
import axios from 'axios';
import SliderSettings from '../../pages/sm_setting/setting12/SliderSettings';
import { logPost } from '../../services/log';
import { prefetchChime, useSpeakerRelay } from '../../hooks/useSpeakerRelay';
import { closeRelaySession } from '../../services/speakerRelayService';
import { getMicPermissionState, MicPermissionState } from '../../utils/micPermission';
import MicPermissionModal from './MicPermissionModal';

const Root = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 5010;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 60%);
  backdrop-filter: blur(0.5rem);
  user-select: none;
  > .modal {
    width: 40rem;
    width: 64rem;
    max-width: calc(100vw - 2rem);
    max-width: calc(100dvw - 2rem);
    border-radius: 0.5rem;
    overflow: hidden;
    max-height: calc(100vh - 2rem);
    max-height: calc(100dvh - 2rem);
    > .header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      background-color: var(--filled-blue-deep);
      color: var(--bw-inverse);
      padding: 0.5rem;
      height: 3.625rem;
      > .title {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0 0.5rem;
      }
      > .closeButton {
        border-radius: 0.5rem;
        margin: 0.375rem;
        padding: 0.25rem;
        display: flex;
        cursor: pointer;
        > .material-symbols-rounded {
          font-variation-settings: 'FILL' 0, 'wght' 600, 'GRAD' 0, 'opsz' 24;
          font-size: 1.375rem;
          color: var(--alpha-50);
        }
        &:hover {
          background-color: var(--primary);
          box-shadow: 0 0.25rem 0.75rem 0 rgba(0, 0, 0, 0.1);
          .material-symbols-rounded {
            color: var(--tonal-deep-blue);
          }
        }
      }
    }
    > .body {
      background-color: var(--tonal);
      display: flex;
      align-items: stretch;
      height: calc(100dvh - 20rem);
      // min-height: 36rem;
      // max-height: calc(100dvh - 9.75rem);
      overflow: hidden;
      position: relative;
      > .bc-side-nav {
        > div:not(.bc-all-volume) {
          height: calc(100% - 75px);
        }

        > .bc-all-volume {
          position: relative;
          height: 75px;
          padding: 0.5rem;
          background: var(--board);
          border-top: 2px solid var(--primary);
          z-index: 10;
        }
      }
      > .content-container {
        max-height: inherit;
        -webkit-overflow-scrolling: touch; /* iOS 기기에서 스크롤 성능 개선 */
        flex: 1 0 60%;
        display: flex;
        flex-direction: column;
        border-left: 1px solid var(--outline);
        background-color: var(--board);

        .tab-head {
          padding: 0.5rem 0;

          h4 {
            font-size: 1rem;
            font-weight: 600;
            color: var(--text-primary);
            line-height: 1.4;
          }
          p {
            font-size: 0.875rem;
            color: var(--text-tertiary);
            word-break: keep-all;
          }
        }

        > .tab-cont {
          display: flex;
          align-items: center;
          justify-content: space-around;
          background-color: var(--board);

          > button.tab {
            flex: 1 0 0%;
            padding: 0.5rem;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 0.5rem;
            font-size: 1rem;
            border-bottom: 1px solid var(--outline);
            color: var(--text-tertiary);
            border-radius: 0;
            cursor: pointer;

            &:hover {
              background-color: var(--tonal);
            }

            &.active {
              // background-color: #eff4ff;
              background-color: var(--tonal-blue);
              color: var(--primary);
            }
          }
        }

        .tab-content {
          overflow-y: auto;
          padding: 0 0.75rem;
          height: calc(100% - 48px);
        }

        .sub-tab-cont {
          display: flex;
          align-items: center;
          position: sticky;
          top: 0;
          background-color: var(--board);
          z-index: 100;

          .sub-tab {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 50%;
            padding: 0.5rem 0;
            color: #adadad;
            border-bottom: 1px solid var(--outline);
            gap: 0.5rem;
            cursor: pointer;

            &.active {
              font-weight: 600;
              color: var(--primary);
              border-color: var(--primary);
            }

            b {
              font-size: 0.875rem;
              padding: 0 8px;
              background-color: var(--primary);
              border-radius: 50%;
              color: #fff;
            }
          }
        }
        .sub-tab-content {
          display: none;
          padding: 0.75rem 0;

          &.active {
            display: block;
          }

          .audio-list-wrap {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
            min-height: 350px;
          }

          .audio-item {
            display: flex;
            align-items: center;
            justify-content: space-between;
            background: var(--tonal);
            border-radius: 0.25rem;
            padding: 0.25rem 0.75rem;

            input[type='checkbox'] {
              margin: 0;
            }

            > div {
              display: flex;
              align-items: center;
              gap: 1rem;
            }

            .play-btn {
              > button {
                height: inherit;
                padding: 6px;
                border-radius: 50%;
                background-color: var(--tonal-blue);

                &:hover {
                  background-color: var(--tonal-deep-blue);
                }

                .material-symbols-rounded {
                  font-variation-settings: 'FILL' 1, 'wght' 400;
                  color: var(--text-primary);
                  background-color: inherit;

                  // &:hover {
                  //   background-color: ${({ theme }: { theme: any }) => theme.tonal_light_blue};
                  // }
                }
              }
            }

            .audio-index {
              color: var(--primary);
            }

            .audio-info-cont {
              display: flex;
              flex-direction: column;

              .audio-name {
                font-size: 0.875rem;
                font-weight: 600;
              }

              .audio-info {
                font-size: 0.875rem;
              }
            }
          }

          .dg-item {
            display: flex;
            align-items: center;
            justify-content: space-between;
            color: var(--text-primary);
            background: var(--tonal);
            border-radius: 0.25rem;
            padding: 0.75rem 0.5rem;

            .dg-left {
              display: flex;
              align-items: center;
              gap: 0.5rem;
            }

            span {
              color: #979797;
            }

            .dg-index {
              color: var(--primary);
            }

            .dg-remove-btn {
              width: 20px;
              height: 20px;
              padding: 0;

              span {
                font-size: 0.875rem;
                color: #fff;
              }
            }
          }
        }

        .volume-test-cont {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: calc(100% - 63px);
          gap: 1.25rem;

          #volumeMeter {
            display: flex;
            width: 200px;
            height: 40px;
            gap: 10px;
          }

          .volumeBar {
            width: 8%;
            border-radius: 4px;
            background-color: darkgray;
          }
        }

        > .informations {
          padding: 0 0.75rem;
          padding-top: 0.75rem;
          display: flex;
          flex-direction: column;
        }
        .textarea-container {
          overflow: auto;
          width: 100%;
          padding-bottom: 0.75rem;
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
          > div {
            top: 0;
          }
        }
      }
    }
    > .footer {
      display: flex;
      justify-content: space-between;
      background-color: var(--board);
      border-top: 1px solid var(--outline);
      padding: 0.5rem;
      > .toggles,
      > .buttons {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        > .label {
          font-size: 0.875rem;
          letter-spacing: normal;
          color: var(--text-tertiary);
          padding: 0 0.5rem;
        }

        .audio-wrap {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .audio-select {
          display: flex;
          align-items: center;
          gap: 0.5rem;

          .defaultOption {
            min-width: 10rem;
          }
        }

        button.play-btn {
          height: inherit;
          padding: 6px;
          border-radius: 50%;
          background-color: var(--tonal-blue);

          &:hover {
            background-color: var(--tonal-deep-blue);
          }

          .material-symbols-rounded {
            font-variation-settings: 'FILL' 1, 'wght' 400;
            color: var(--text-primary);
            background-color: inherit;
          }
        }
      }
    }
  }
  > .cctvWrapperHidden {
    visibility: hidden;
    height: 0.1rem;
    width: 0.1rem;
    position: absolute;
    left: 0;
    top: 0;
  }

  .bc-tts-btn {
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: var(--primary);
    cursor: pointer;
    gap: 0.25rem;

    > b {
      font-size: 0.875rem;
      text-decoration: underline;
      text-underline-offset: 3px;
    }

    > .material-symbols-rounded {
      font-size: 0.875rem;
    }
  }
`;

const SwitchToggleWrapper = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  height: 2.5rem;
  padding: 0 1rem;
  border-radius: 2rem;
  gap: 0.375rem;
  border: 2px solid var(--tonal-deep);
  color: var(--text-tertiary);

  .text {
    font-size: 0.875rem;
    letter-spacing: normal;
  }

  &.disabled {
    background-color: var(--tonal);
    cursor: default;
  }

  &.red {
    background-color: var(--tonal-deep-red);
    border-color: var(--tonal-light-red);

    &:hover {
      border: 2px solid var(--tonal-deep-red);
      background-color: var(--tonal-red);
      color: var(--filled-red);
    }
  }

  &.green {
    background-color: var(--tonal-deep-green);

    &:hover {
      border: 2px solid var(--tonal-deep-green);
      background-color: var(--tonal-green);
      color: var(--filled-green);
    }
  }
`;

interface IProps {
  openModal: any;
  setOpenModal: Dispatch<SetStateAction<IModal>>;
  isTunnel?: boolean;
}

const DraggableRow = ({ type, el, auth, index, moveRow, tableState, setTableState, onClickRow, selectedKey, setAudioPlayList }: any) => {
  const trRef = useRef(null);
  const { t } = useTranslation();
  const userInfo = useRecoilValue(userState);
  const newCdArray = tableState.map((elParam: any) => elParam.cdSort);
  const cdSortArray = newCdArray.sort((a: number, b: number) => a - b);

  const onClickRemove = () => {
    const tableArray = [...tableState];
    tableArray.splice(index, 1);
    setTableState(tableArray);

    const audioPlayList = tableArray.map((audio: any) => {
      const newAudio = new Audio();
      audio.crossOrigin = 'anonymous';
      audio.src = audio.bsfFurl;
      return audio;
    });
    setAudioPlayList(audioPlayList);
  };

  const [, drop] = useDrop({
    accept: 'ROW',
    hover(item: any, monitor) {
      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }
      moveRow(tableState, setTableState, dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
    drop: (item, monitor) => {
      const copyArray = [...tableState];
      const newArray = copyArray.map((cpItem, cpIndex) => ({ ...cpItem, cdSort: cdSortArray[cpIndex], editor: userInfo.userId }));
      setTableState(newArray);

      const audioPlayList = newArray.map((audio: any) => {
        const newAudio = new Audio();
        newAudio.crossOrigin = 'anonymous';
        newAudio.src = audio.bsfFurl;
        return newAudio;
      });
      setAudioPlayList(audioPlayList);
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: 'ROW',
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  drag(drop(trRef));

  return (
    <div ref={trRef} className={`dg-item ${selectedKey && el.tatCd === selectedKey ? 'selected' : ''}`} style={{ opacity: isDragging ? 0 : 1 }} role='presentation' key={el.bsfNameFormat}>
      <div className='dg-left'>
        <span className='material-symbols-rounded'>drag_handle</span>
        <div className='dg-index'>{index + 1}</div>
        <div className='dg-name'>{el.bsfNameFormat}</div>
      </div>
      <BtnRed onClick={onClickRemove} className='dg-remove-btn'>
        <span className='material-symbols-rounded'>remove</span>
      </BtnRed>
    </div>
  );
};

const dragRow = (list: any[], setList: Dispatch<SetStateAction<any[]>>, fromIndex: number, toIndex: number) => {
  const updatedData = [...list];
  const [movedItem] = updatedData.splice(fromIndex, 1);
  updatedData.splice(toIndex, 0, movedItem);
  setList(updatedData);
};

const VOICE_TAB = 0;
const AUDIO_TAB = 1;
const TTS_TAB = 2;

const SUB_SELECT_TAB = 0;
const SUB_ORDER_TAB = 1;

const BroadcastLiveModal = ({ openModal, setOpenModal, isTunnel }: IProps) => {
  const [checkSpeakerLengthState, setCheckSpeakerLengthState] = useState(openModal.data.length);
  const [checkedSpeakerSet, setCheckedSpeakerSet] = useState(new Set<number>());
  const [failedSpeakerSet, setFailedSpeakerSet] = useState(new Set<number>());
  const { t } = useTranslation();
  const isKorean = i18n.language === 'ko-KR';
  const { auth } = useSetAuth();
  const mobile = isMobile();
  const userInfo = useRecoilValue(userState);

  const INIT_OPEN_SUB_MODAL = { status: false, type: '', isOverlappingModal: true };
  const [openSubModal, setOpenSubModal] = useState<any>(INIT_OPEN_SUB_MODAL);
  const [activeTab, setActiveTab] = useState<number>(VOICE_TAB);
  // 마이크 권한 안내 모달 상태 (null이면 닫힘)
  const [micGuideState, setMicGuideState] = useState<Exclude<MicPermissionState, 'granted'> | null>(null);

  const [speakerList, setSpeakerList] = useState<WebSDKspeaker[]>([]);
  const [isPlaying, setIsPlaying] = useState<boolean>(false); // 재생중인 여부

  const [isLiveBroadcast, setIsLiveBroadcast] = useState(false); // 방송중 여부
  const [isMicEnabled, setIsMicEnabled] = useState(false); // 마이크 방송 사용 여부

  const [broadcastState, setBroadcastState] = useState<{ index: number; connectValue: number }[]>([{ index: 0, connectValue: 0 }]);
  const [selectSpeaker, setSelectSpeaker] = useState<number[]>([]);
  const [inputState, setInputState] = useState({ ko: '', en: '', ch: '', vi: '' });

  const [isStartAudioEnabled, setIsStartAudioEnabled] = useState(true);
  const [isEndAudioEnabled, setIsEndAudioEnabled] = useState(true);
  const [audioList, setAudioList] = useState<any[]>([]);
  const AUDIO_DELAY = 500;
  let onFrameId: number | null = null;

  let audioObjRef = useRef<HTMLAudioElement | null>(null);
  const broadcastPlayListRef = useRef<HTMLAudioElement[]>([]);
  const isMicEnabledRef = useRef(false);
  const stoppedByUserRef = useRef(false); // 사용자가 직접 방송을 정지시킨 것인지 여부 (음원 재생 실패와 구분하기 위함)
  const { relaySessionIdRef, startPluginRelay, startAudioCapture, stopAudioCapture, stopPluginRelay, playChime, routeAudioElementThroughRelay } = useSpeakerRelay();

  const [isLoading, setIsLoading] = useState(true);
  const [isMicState, setIsMicState] = useState(false); // 마이크 테스트 상태 여부
  const [streamObj, setStreamObj] = useState<MediaStream | null>(null); // Stream 객체를 저장하기 위한 상태 변수

  const [audioPlayList, setAudioPlayList] = useState<HTMLAudioElement[]>([]);
  const [currentAudioIndex, setCurrentAudioIndex] = useState(0);
  const [isUnChkAll, setIsUnChkAll] = useState(false);
  const { hCd, sCd } = userInfo;
  const [disableBtn, setDisableBtn] = useState(false);

  const [subCurrentTab, setSubCurrentTab] = useState(SUB_SELECT_TAB);
  const [audioCheckList, setAudioCheckList] = useState<any[]>([]);

  const [selectAudio, setSelectAudio] = useState<any>({}); // 각 음원 재생 버튼 클릭시 체크용
  const [bsData, setBsData] = useState<BsData[]>([]);
  const [totalVolume, setTotalVolume] = useState<number>(0);
  const [volumeLoad, setVolumeLoad] = useState<boolean>(true);
  const sliderTimeout = useRef<NodeJS.Timeout | null>(null);
  // 렌더마다 새 Audio 를 만들면 정지/ended 리스너가 실제 재생 중인 객체와 어긋나므로 ref 로 1회만 생성한다
  const startAudioRef = useRef<HTMLAudioElement | null>(null);
  if (!startAudioRef.current) startAudioRef.current = createChimeAudio(START_CHAIM_AUDIO);
  const startAudio = startAudioRef.current;

  const startAudioForAudioTabRef = useRef<HTMLAudioElement | null>(null);
  if (!startAudioForAudioTabRef.current) startAudioForAudioTabRef.current = createChimeAudio(START_CHAIM_AUDIO);
  const startAudioForAudioTab = startAudioForAudioTabRef.current;
  const ttsKoList = JSON.parse(localStorage.getItem('ttsKo') || '[]');
  const ttsEnList = JSON.parse(localStorage.getItem('ttsEn') || '[]');
  const ttsChList = JSON.parse(localStorage.getItem('ttsCh') || '[]');
  const ttsViList = JSON.parse(localStorage.getItem('ttsVi') || '[]');

  const componentRef = useRef<HTMLDivElement>(null);

  const Backend = mobile ? TouchBackend : HTML5Backend;

  let checkSpeaker = 0; // 모든 장비의 방송가능여부 체크가 완료됐는지 확인하는 용도

  const { data: playerListData, isLoading: playerListLoading } = useQuery(['broadScheduleGetApi', userInfo.hCd], () => broadScheduleGetApi(), {
    enabled: !!userInfo.hCd && !!userInfo.sCd,
  });

  // 방송장비 목록 조회
  const broadScheduleGetApi = async () => {
    const req = { hCd: userInfo.hCd, sCd: userInfo.sCd };
    const res = await apiGet({ path: '/bs/bsf', req });

    if (res.status === 200) {
      const newMap = res.data.data.map((el: any) => ({
        ...el,
        type: 'playerCd',
        playerCd: el.bsfCd,
        cdName: `${el.bsfName}.${el.audioType}`,
        bsfFsizeMb: `${el.bsfFsize}MB`,
        bsfNameFormat: `${el.bsfName}.${el.audioType}`,
        bsfDuration: el.bsfFtime.match(/.{1,2}/g).join(':'),
      }));
      const useYnFilter = newMap.filter((item: any) => item.useYn === 'Y');

      return useYnFilter;
    }
    return res;
  };

  // 음원 정지
  const onClickStopBtn = () => {
    if (audioObjRef.current) {
      // audioObjRef.current.removeEventListener('ended', () => handleAudioEnd('preview'));
      audioObjRef.current.pause();
      audioObjRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  };

  const playAudio = async (index: number) => {
    // 사용자가 이미 방송을 정지시켰다면 예약되어 있던 다음 곡 재생을 무시
    if (stoppedByUserRef.current) return;
    const list = broadcastPlayListRef.current;
    if (index < list.length) {
      audioObjRef.current = list[index];
      routeAudioElementThroughRelay(audioObjRef.current);
      audioObjRef.current
        .play()
        .then(() => {})
        .catch((err) => {
          // 사용자가 직접 정지시켜서 발생한 중단(pause)은 다음 곡으로 넘기지 않음
          if (stoppedByUserRef.current) return;
          handleAudioEnd();
          console.error(err);
          toast.error(t('음원 파일이 존재하지 않아 방송을 곧 중지합니다.'));
        });
      setIsPlaying(true);
      audioObjRef.current.addEventListener('ended', () => handleAudioEnd(), { once: true });
    } else {
      setIsPlaying(false);

      const broadcastingSpeakers = getBroadcastingSpeakers(speakerList);
      setCheckSpeakerLengthState(broadcastingSpeakers.length); // 수정된 코드 [ 체크된 스피커 장비 개수 ]
      // setCheckSpeakerLengthState(speakerList.length); 이전 코드 [ 상태 상관없이 전체 스피커 장비 개수 ]

      // 2. 종료음 재생 (사용자 설정 활성화 시)
      if (isEndAudioEnabled) {
        let pauseRan = false;
        const pause = async () => {
          if (pauseRan) return;
          pauseRan = true;
          await doStopBroadcast(broadcastingSpeakers);
          setIsMicEnabled(false);
          setIsLiveBroadcast(false);
        };
        // 로컬 endAudio 와 relay 송출은 서로 다른 파이프라인이라 끝나는 시점이 다르다.
        // relay 쪽 재생이 끝나기 전에 stopAudioCapture 하면 스피커에서 종료음이 잘리므로
        // relay 재생 완료를 기다린 뒤, 마지막 오디오 청크 전달 여유 500ms 를 두고 정리한다.
        const relayChimeDone = playChime(END_CHAIM_AUDIO);
        // endAudio 는 모듈 싱글턴이라 addEventListener 로 걸면 종료 경로가 겹칠 때
        // 핸들러가 누적돼 teardown 이 중복 실행된다. onended 대입으로 항상 하나만 유지한다.
        endAudio.onended = async () => {
          endAudio.onended = null;
          await relayChimeDone;
          setTimeout(pause, 500);
        };
        endAudio.play().catch((e) => {
          // 종료음 재생 실패 시에도 장비 정지 및 로딩 해제가 되도록 fallback (relay 종료음 송출 완료 후 정리)
          console.error(e);
          endAudio.onended = null;
          relayChimeDone.then(() => setTimeout(pause, 500));
        });
      } else {
        await doStopBroadcast(broadcastingSpeakers);
        setIsMicEnabled(false);
        setIsLiveBroadcast(false);
      }
    }
  };

  const handleAudioEnd = () => {
    const currentIndex = broadcastPlayListRef.current.indexOf(audioObjRef.current!);
    setTimeout(() => {
      playAudio(currentIndex + 1);
    }, 500);
  };

  const startPlaying = () => {
    onClickStopAudio();
    if (audioPlayList.length === 0) {
      toast.warn('음원을 선택해주세요');
      return;
    }
    if (activeTab !== AUDIO_TAB) {
      return;
    }
    // 방송마다 새 Audio 요소를 생성해야 함: createMediaElementSource()는 요소당 한 번만 호출 가능하며
    // 이전 방송의 AudioContext가 닫히면 기존 요소는 재사용 불가
    broadcastPlayListRef.current = audioPlayList.map((el) => {
      const fresh = new Audio();
      fresh.crossOrigin = 'anonymous';
      fresh.src = el.src;
      return fresh;
    });
    playAudio(0);
  };

  type BsData = {
    hCd: string;
    sCd: string;
    bsCd: string;
    bsId: string;
    bsIp: string;
    bsName: string;
    bsPwd: string;
    cName: null | string;
    checked: boolean;
    pPort: string;
    tadCd: string;
    tadName: string;
    tatCd: string;
    tatGubun: string;
    volume: number;
  };

  // 방송 도중 네트워크 지연으로 채임이 유실되지 않도록 모달 진입 시 미리 받아둔다.
  // 특히 종료음은 로컬 재생이 끝나면 곧바로 relay 를 내리기 때문에 선행 로드가 필요하다.
  useEffect(() => {
    prefetchChime(START_CHAIM_AUDIO).catch((err) => console.error('[prefetchChime] start', err));
    prefetchChime(END_CHAIM_AUDIO).catch((err) => console.error('[prefetchChime] end', err));
  }, []);

  useEffect(() => {
    const bsVolumes = new Map<number, number>();
    setBsData(
      openModal.data.map((el: any) => ({
        ...el,
        hCd: userInfo.hCd,
        sCd: userInfo.sCd,
        volume: 0,
      }))
    );

    setIsLoading(true);
    // 방송 장비 정보 및 연결 상태 초기화
    initSpeaker().then(async (res) => {
      if (res.length > 0) {
        setSpeakerList(res);
        /* eslint-disable no-await-in-loop */
        const broadcastStateList = openModal.data.map((_: any, idx: number) => ({ index: idx, connectValue: 0 }));
        setBroadcastState(broadcastStateList);
        for (let i = 0; i < res.length; i += 1) {
          await res[i].login();
          const volumes = await apiGet({ path: '/bs/volume', req: { hCd: userInfo.hCd, sCd: userInfo.sCd, bsCd: openModal.data[i].bsCd }, timeout: 3000 });
          if (volumes?.data?.data) {
            bsVolumes.set(i, volumes.data.data.outputVolume);
          }
        }
        setBsData(
          openModal.data.map((el: any, idx: number) => ({
            ...el,
            hCd: userInfo.hCd,
            sCd: userInfo.sCd,
            volume: bsVolumes.get(idx),
          }))
        );
        setVolumeLoad(false);
      } else {
        const broadcastStateList = openModal.data.map((_: any, idx: number) => ({ index: idx, connectValue: 3 }));
        setBroadcastState(broadcastStateList);
      }
    });
  }, [openModal.data]);

  const stateChangeListener = (params: any) => {
    // console.log('--------- : ', params);

    setCheckedSpeakerSet((prev) => {
      const newSet = new Set(prev);
      newSet.add(params.index);
      return newSet;
    });

    const newBsState = broadcastState;
    if (params.status === 1 || params.status === 2) {
      setSelectSpeaker((prev) => [...prev, params.index]);
    }
    newBsState[params.index] = { index: params.index, connectValue: params.status };
    setBroadcastState([...newBsState]);
  };

  useEffect(() => {
    isMicEnabledRef.current = isMicEnabled;
  }, [isMicEnabled]);

  useEffect(() => {
    // console.log(checkedSpeakerSet.size, '/', checkSpeakerLengthState);
    if (checkedSpeakerSet.size >= checkSpeakerLengthState) {
      setIsLoading(false);
    }
  }, [checkedSpeakerSet.size, checkSpeakerLengthState]);

  // 새로고침 / 탭 닫기 시 오디오 전송 중단
  useEffect(() => {
    const handleBeforeUnload = () => {
      stopAudioCapture();
      if (relaySessionIdRef.current) {
        closeRelaySession(relaySessionIdRef.current);
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // 페이지 이동(라우터) 시 컴포넌트 언마운트 cleanup
  useEffect(() => {
    return () => {
      stopAudioCapture();
      if (relaySessionIdRef.current) {
        closeRelaySession(relaySessionIdRef.current);
      }
    };
  }, []);

  const initSpeaker = async () => {
    let newWebsdkSpeakers: WebSDKspeaker[] = [];

    const speakers = openModal.data;
    let initFailChk = false;
    /* eslint-disable no-await-in-loop */
    for (let i = 0; i < speakers.length; i += 1) {
      const { bsIp: ip, pPort: port, bsId: id, bsPwd: pwd, bsName, tadName } = speakers[i];
      const equipmentInfo = { ip, port, id, pwd, bsName, tadName };
      const websdkSpeaker = new WebSDKspeaker({ setOpenModal: setOpenSubModal, index: i, stateChangeListener, data: equipmentInfo, divPlugin: `divPlugin${i}` });
      await websdkSpeaker
        .init()
        .then(() => {
          newWebsdkSpeakers.push(websdkSpeaker);
        })
        .catch((e) => {
          setIsLoading(false);
          initFailChk = true;
        });
      if (initFailChk) {
        toast.error(t('방송장비 연결에 실패했습니다.'));
        setIsLoading(false);
        break;
      }
    }
    return newWebsdkSpeakers;
  };
  /**
   * 방송 시작 전 마이크 권한 확인.
   * 서버 릴레이 방식은 마이크 스트림 기반 mixBus로 오디오를 전송하므로
   * 음성 / 음원 / TTS 탭 모두 권한이 없으면 장비로 소리가 나가지 않는다.
   * 권한이 없으면 안내 모달을 띄우고 방송 시작을 중단한다.
   */
  const checkMicPermission = async (): Promise<boolean> => {
    const permission = await getMicPermissionState();
    if (permission === 'granted') return true;

    setMicGuideState(permission);
    return false;
  };

  const onClickStartBroadcast = async () => {
    try {
      const isMicAllowed = await checkMicPermission();
      if (!isMicAllowed) return;

      if (selectSpeaker.length > 0) {
        setCheckedSpeakerSet(new Set());
        setIsLoading(true);
        const possibleSpeakers = broadcastState.filter((el) => el.connectValue === 1); // 방송 가능한 장비만 필터링
        const selectedSpeakers = possibleSpeakers.map((el) => speakerList.find((sp) => sp.getIndex() === el.index && selectSpeaker.includes(el.index))).filter((sp): sp is WebSDKspeaker => !!sp);
        // const selectedSpeakers = broadcastState.map((el) => speakerList.find((sp) => sp.getIndex() === el.index && selectSpeaker.includes(el.index))).filter((sp): sp is WebSDKspeaker => !!sp)

        setCheckSpeakerLengthState(selectedSpeakers.length);
        await startBroadcast(selectedSpeakers);

        saveTTS();
      } else {
        toast.warning(t('방송을 실행할 장비를 선택해주세요'));
      }
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  const saveTTS = () => {
    if (inputState.ko) {
      const ttsKo = JSON.parse(localStorage.getItem('ttsKo') || '[]');
      localStorage.setItem('ttsKo', JSON.stringify([inputState.ko, ...ttsKo.filter((el: string) => el !== inputState.ko)].slice(0, 5)));
    }
    if (inputState.en) {
      const ttsEn = JSON.parse(localStorage.getItem('ttsEn') || '[]');
      localStorage.setItem('ttsEn', JSON.stringify([inputState.en, ...ttsEn.filter((el: string) => el !== inputState.en)].slice(0, 5)));
    }
    if (inputState.ch) {
      const ttsCh = JSON.parse(localStorage.getItem('ttsCh') || '[]');
      localStorage.setItem('ttsCh', JSON.stringify([inputState.ch, ...ttsCh.filter((el: string) => el !== inputState.ch)].slice(0, 5)));
    }
    if (inputState.vi) {
      const ttsVi = JSON.parse(localStorage.getItem('ttsVi') || '[]');
      localStorage.setItem('ttsVi', JSON.stringify([inputState.vi, ...ttsVi.filter((el: string) => el !== inputState.vi)].slice(0, 5)));
    }
  };

  // 시작 음성 종료시 실행할 핸들러
  const handleStartAudioEnded = () => {
    if (activeTab !== AUDIO_TAB) return;
    startPlaying();
  };

  // 방송 시작 핸들러
  const startBroadcast = async (selectedSpeakers: WebSDKspeaker[]) => {
    // 방송 시작 핸들러
    if (disableBtn) {
      toast.warning(t('잠시 기다려주세요.'));
      setIsLoading(false);
      return;
    }

    // 마이크가 켜져 있는 경우 경고
    if (isMicEnabled) {
      setIsLoading(false);
      return;
    }

    // 연결 중인 방송 장비가 있는 경우 경고
    if (hasConnectingSpeaker(selectedSpeakers)) {
      toast.warning(t('연결중입니다'));
      setIsLoading(false);
      return;
    }

    // 사용 가능한 방송 장비가 없는 경우 경고
    if (hasAvailableSpeaker(selectedSpeakers)) {
      toast.warning(t('방송을 실행할 수 있는 장비가 없습니다'));
      setIsLoading(false);
      return;
    }

    // 음원 방송에서 선택된 음원이 없는 경우 경고
    if (activeTab === AUDIO_TAB && audioPlayList.length === 0) {
      toast.warning(t('음원을 선택해주세요'));
      setIsLoading(false);
      return;
    }

    // TTS 탭에서 입력값이 없는 경우 경고
    if (activeTab === TTS_TAB && isInputEmpty(inputState)) {
      toast.warning(t('TTS 방송 내용을 입력해주세요'));
      setIsLoading(false);
      return;
    }

    // 재생중인 음원 정지
    if (audioObjRef.current) {
      audioObjRef.current.pause();
      audioObjRef.current.currentTime = 0; // Reset playback
    }

    // 이전 오디오의 ended 이벤트 리스너 제거
    // startAudio.removeEventListener('ended', handleStartAudioEnded);

    // 새 방송 시작이므로 정지 플래그 초기화 (이후 재생 실패는 정상적으로 다음 곡으로 넘어가야 함)
    stoppedByUserRef.current = false;

    setIsLiveBroadcast(true); // 방송중 상태 활성화

    // 1. 실행 가능한 방송 장비 시작
    setDisableBtn(true);
    const connectedSpeakers = getConnectedSpeakers(selectedSpeakers);

    if (connectedSpeakers.length >= 1) {
      let sessionId: string;
      try {
        sessionId = await startPluginRelay(connectedSpeakers);
      } catch {
        toast.error(t('릴레이 세션 생성에 실패했습니다.'));
        setDisableBtn(false);
        setIsLoading(false);
        setIsLiveBroadcast(false);
        return;
      }

      // 브라우저 오디오 → G.711µ-law → relay PUT /data (전 탭 공통)
      // startAudioCapture는 mixBus를 초기화하므로 playChime보다 먼저 await
      try {
        await startAudioCapture(sessionId);
      } catch (err) {
        if (activeTab === VOICE_TAB) toast.error(t('마이크 접근에 실패했습니다.'));
        console.error('[browser audio relay]', err);
      }
      // JS_StartTalk 완료(relay open URL 응답) 이후에도 relay → 장비 ISAPI
      // 오디오 파이프가 완전히 열리는 데 추가 시간이 필요.
      // 대기 없이 chime을 보내면 장비가 수신 준비가 안 돼 첫 음이 소실됨.
      await new Promise<void>((r) => {
        setTimeout(r, 500);
      });
    }
    setDisableBtn(false);

    // 마이크 음성 방송
    if (activeTab === VOICE_TAB) {
      audioObjRef.current = null;
      if (isStartAudioEnabled) {
        startAudio.play();
        playChime(START_CHAIM_AUDIO);
      }
    }

    // 음원 방송
    if (activeTab === AUDIO_TAB) {
      if (isStartAudioEnabled) {
        playChime(START_CHAIM_AUDIO);
        startAudioForAudioTab
          .play()
          .then(() => {
            if (activeTab === AUDIO_TAB) {
              // addEventListener 대신 onended 대입: 방송을 중간에 정지하면 ended 가 발생하지 않아
              // { once: true } 리스너가 해제되지 않고 다음 방송 때 중복 호출된다.
              startAudioForAudioTab.onended = handleStartAudioEnded;
            }
          })
          .catch(() => {
            setTimeout(() => {
              startAudioForAudioTab.play();
            }, 500);
          });
      } else {
        startPlaying();
      }
    }

    // TTS 방송
    if (activeTab === TTS_TAB) {
      if (isStartAudioEnabled) playChime(START_CHAIM_AUDIO);
      playTTS({
        inputState,
        toggleStartAudio: isStartAudioEnabled,
        setAudioList,
        onClickStop: stopBroadcast,
        routeAudio: routeAudioElementThroughRelay,
      });
    }

    setIsMicEnabled(true); // 마이크 사용 여부 설정
  };

  const doStopBroadcast = async (broadcastingSpeakers: WebSDKspeaker[]) => {
    stopAudioCapture();
    await stopPluginRelay(broadcastingSpeakers);
  };

  // 방송 정지 핸들러
  const stopBroadcast = async () => {
    if (disableBtn) {
      toast.warning(t('잠시 기다려주세요.'));
      return;
    }
    // 마이크 사용 상태 활성화 시 (ref 사용: 클로저 stale 방지)
    if (!isMicEnabledRef.current) return;
    // 사용자가 직접 정지시킨 것으로 표시 (재생 실패로 오인해 다음 곡으로 넘어가는 것 방지)
    stoppedByUserRef.current = true;
    // 1. 재생 중인 모든 오디오 정지

    onClickStopBtn();
    setCheckedSpeakerSet(new Set());
    setIsLoading(true);

    const broadcastingSpeakers = getBroadcastingSpeakers(speakerList);
    setCheckSpeakerLengthState(broadcastingSpeakers.length);

    startAudioForAudioTab.pause();
    startAudioForAudioTab.currentTime = 0;
    // 정지 시엔 시작음이 끝난 것이 아니므로 후속 음원 재생 핸들러를 해제한다
    startAudioForAudioTab.onended = null;
    audioList.length > 0 &&
      audioList.forEach((audio) => {
        audio.pause();
        audio.currentTime = 0;
      });

    // 2. 종료음 재생 (사용자 설정 활성화 시)
    setDisableBtn(true);
    if (isEndAudioEnabled) {
      let pauseRan = false;
      const pause = async () => {
        if (pauseRan) return;
        pauseRan = true;
        if (broadcastingSpeakers.length > 0) {
          await doStopBroadcast(broadcastingSpeakers);
          setIsMicEnabled(false);
          setIsLiveBroadcast(false);
        } else {
          setIsLoading(false);
          setIsMicEnabled(false);
          setIsLiveBroadcast(false);
        }
      };
      // 로컬 endAudio 와 relay 송출은 서로 다른 파이프라인이라 끝나는 시점이 다르다.
      // relay 쪽 재생이 끝나기 전에 stopAudioCapture 하면 스피커에서 종료음이 잘리므로
      // relay 재생 완료를 기다린 뒤, 마지막 오디오 청크 전달 여유 500ms 를 두고 정리한다.
      const relayChimeDone = playChime(END_CHAIM_AUDIO);
      // endAudio 는 모듈 싱글턴이라 addEventListener 로 걸면 종료 경로가 겹칠 때
      // 핸들러가 누적돼 teardown 이 중복 실행된다. onended 대입으로 항상 하나만 유지한다.
      endAudio.onended = async () => {
        endAudio.onended = null;
        await relayChimeDone;
        setTimeout(pause, 500);
      };
      endAudio.play().catch((e) => {
        // 종료음 재생 실패 시에도 장비 정지 및 로딩 해제가 되도록 fallback (relay 종료음 송출 완료 후 정리)
        console.error(e);
        endAudio.onended = null;
        relayChimeDone.then(() => setTimeout(pause, 500));
      });
    } else {
      await doStopBroadcast(broadcastingSpeakers);
      setIsMicEnabled(false);
      setIsLiveBroadcast(false);
    }
    setDisableBtn(false);

    // 모든 음원 재생 정지
    // onClickStopAudio();
  };

  // 모달창 닫기 핸들러
  const onClickClose = () => {
    const checkMic = broadcastState.filter((state: any) => state === 2);
    // 1. 방송 중인지 확인 후 경고 메시지 출력
    if (isLoading) {
      toast.warning(t('연결 중입니다.'));
      return;
    }
    /**
     * 수정자 : 한영광
     * 수정일자 : 2024.10.02
     * 수정내용 : 방송 중인 경우 종료하지 못하게 하는 경우 방송 중지가 안될 때 종료할 방법이 없어서 주석처리함
     */
    // if (isMicEnabled || checkMic.length > 0) {
    //   toast.warning(t('방송 중입니다. 먼저 방송을 중지해주세요.'));
    //   return;
    // }
    // 1. 모든 오디오 요소 중지
    stopAudio(audioList);
    // 2. 재생중인 음원 중지
    onClickStopAudio();
    // 릴레이 세션 정리
    stopAudioCapture();
    if (relaySessionIdRef.current) {
      closeRelaySession(relaySessionIdRef.current);
      relaySessionIdRef.current = null;
    }
    // 필요 시 모달 닫기
    !openSubModal.status && setOpenModal((prev) => ({ ...prev, status: false }));
    // 방송 장비 중지 및 로그아웃
    stopSpeakers(speakerList);
  };

  // openModal이 존재하고 data가 배열인 경우
  const isOpenModalData = openModal?.data && Array.isArray(openModal.data);
  // openModal이 null 또는 undefined인 경우
  if (!isOpenModalData) return null;

  // 탭 변경 핸들러
  const onClickTab = (tab: number) => {
    if (isLoading) {
      toast.warning(t('방송장비를 불러오는 중입니다.'));
      return;
    }
    if (isLiveBroadcast) {
      toast.warning(t('방송 중에 탭을 변경할 수 없습니다.'));
      return;
    }

    activeTab !== tab && onClickStopBtn(); // 탭 변경 시 음원 정지
    setActiveTab(tab);
  };

  // 마이크 테스트 시작
  const startRecording = async () => {
    const isMicAllowed = await checkMicPermission();
    if (!isMicAllowed) return;

    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        setIsMicState(true);
        setStreamObj(stream);

        const context = new AudioContext();
        const analyser = context.createAnalyser();
        const mediaStreamAudioSourceNode = context.createMediaStreamSource(stream);
        mediaStreamAudioSourceNode.connect(analyser, 0);
        const pcmData: any = new Float32Array(analyser.fftSize);

        const onFrame = () => {
          analyser.getFloatTimeDomainData(pcmData);
          let sum = 0.0;
          for (const amplitude of pcmData) {
            sum += amplitude * amplitude;
          }
          const rms = Math.sqrt(sum / pcmData.length);
          const normalizedVolume = Math.min(1, rms / 0.5);
          colorVolumeMeter(normalizedVolume * 2);
          onFrameId = window.requestAnimationFrame(onFrame);
        };

        onFrameId = window.requestAnimationFrame(onFrame);
      })
      .catch((error) => {
        console.error('마이크 권한 획득 실패', error);
      });
  };

  // 마이크 테스트 종료
  const stopRecording = () => {
    if (onFrameId) window.cancelAnimationFrame(onFrameId);
    if (streamObj) {
      streamObj.getTracks().forEach((track: any) => track.stop());
      setIsMicState(false);
    }
  };

  const normalizeToInteger = (volume: any, min: number, max: number) => {
    const scaledValue = Math.min(max, Math.max(min, volume * (max - min) + min));
    return Math.round(scaledValue);
  };

  const colorVolumeMeter = (vol: any) => {
    const VOL_METER_MAX = 10;
    const childrens = document.querySelectorAll('.volumeBar') as NodeListOf<HTMLElement>;

    childrens.forEach((child) => {
      child.style.backgroundColor = '#e6e6e6';
    });

    const numberOfChildToColor = normalizeToInteger(vol, 0, VOL_METER_MAX);
    const coloredChild = Array.from(childrens).slice(0, numberOfChildToColor);

    coloredChild.forEach((child) => {
      child.style.backgroundColor = '#2563eb';
    });
  };

  // 음원파일 체크박스
  const onCheckAudio = (e: ChangeEvent<HTMLInputElement>, audioItem: any) => {
    if (e.target.checked) {
      const isCheckAdd = !audioCheckList.some((item: any) => audioItem.bsfCd === item.bsfCd) ? audioItem : null;
      const newChekList = [...audioCheckList, isCheckAdd];
      const newMap = newChekList.map((item: any) => {
        const el = new Audio();
        el.crossOrigin = 'anonymous';
        el.src = item.bsfFurl;
        return el;
      });

      setAudioCheckList((prev: any) => [...prev, isCheckAdd]);
      setAudioPlayList(newMap);
    } else {
      const removeItem = audioCheckList.filter((item: any) => audioItem.bsfCd !== item.bsfCd);
      const newMap = removeItem.map((item: any) => {
        const el = new Audio();
        el.crossOrigin = 'anonymous';
        el.src = item.bsfFurl;
        return el;
      });

      setAudioCheckList(removeItem);
      setAudioPlayList(newMap);
    }
  };

  // 각 음원 재생 클릭 이벤트
  const onClickPlayAudio = (audioItem: any) => {
    setSelectAudio(audioItem);
    onClickStopBtn();

    const audioObj = new Audio(audioItem.bsfFurl);
    audioObj.crossOrigin = 'anonymous';

    audioObjRef.current = audioObj;
    audioObjRef.current
      .play()
      .then(() => {
        const handleEnd = () => {
          setSelectAudio({});
          onClickStopBtn();
        };
        audioObjRef.current?.addEventListener('ended', handleEnd);
      })
      .catch((err) => {
        setSelectAudio({});
        console.error(err);
        toast.error(t('해당 음원 파일이 존재하지 않거나 재생에 문제가 발생하여 재생할 수 없습니다.'));
      });
  };

  const onClickStopAudio = () => {
    setSelectAudio({});
    onClickStopBtn();
  };

  const onClickAlarmStop = async () => {
    try {
      await axios.get(`${process.env.REACT_APP_BASE_URL}/bs/alarmstop?hCd=${hCd}&sCd=${sCd}`);
      await axios.get(`${process.env.REACT_APP_BASE_URL}/bs/schedulestop?hCd=${hCd}&sCd=${sCd}`);
      toast.success(t('알람 중지 성공'));
      await logPost({
        hCd: userInfo.hCd,
        sCd: userInfo.sCd,
        userId: userInfo.userId,
        menu: '알람중지',
        action: '알람중지',
        etc: `현장 : ${userInfo.sName}`,
      });
    } catch (error) {
      toast.error(t('알람 중지 실패'));
    }
  };

  const renderSdkPlugin = (dataList: any[]) => {
    return (
      <>
        {dataList.map((item: any, index: number) => (
          <div id={`divPlugin${index}`} className='cctvWrapperHidden' key={index} />
        ))}
      </>
    );
  };

  // 슬라이더 값 처리
  const handleSliderValue = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setTotalVolume(parseInt(e.target.value, 10));

    if (sliderTimeout.current) {
      clearTimeout(sliderTimeout.current);
    }

    sliderTimeout.current = setTimeout(async () => {
      await Promise.all(
        bsData.map(async (bs: any, idx: number) => {
          if (broadcastState[idx].connectValue === 1 || broadcastState[idx].connectValue === 2) {
            await apiPatch({
              path: '/bs/volume',
              req: {
                hCd: bs.hCd,
                sCd: bs.sCd,
                bsCd: bs.bsCd,
                volume: Number(e.target.value),
              },
              contentType: 'application/json',
            });
          }
        })
      );
      setBsData((prev: any) =>
        prev.map((bs: any, idx: number) => {
          if (broadcastState[idx].connectValue === 1 || broadcastState[idx].connectValue === 2) {
            return { ...bs, volume: Number(e.target.value) };
          }
          return bs;
        })
      );
    }, 500);
  };

  return (
    <Root>
      {openSubModal.status !== true && (
        <div
          className='modal'
          role='presentation'
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <div className='header'>
            <span className='title'>{t('실시간 방송')}</span>
            {!isLoading && (
              <div className='closeButton' onClick={onClickClose} role='presentation'>
                <span className='material-symbols-rounded'>close</span>
              </div>
            )}
          </div>
          <div className='body'>
            <div className='bc-side-nav'>
              <BroadcastEquipList
                data={bsData}
                state={broadcastState}
                setState={setBroadcastState}
                isTunnel={isTunnel}
                selectSpeaker={selectSpeaker}
                setSelectSpeaker={setSelectSpeaker}
                isLiveBroadcast={isLiveBroadcast}
                isLoading={isLoading}
              />
              <div className='bc-all-volume'>
                <SliderSettings labelName='전체 볼륨' data={`${totalVolume}`} dataKey='volume' handleSlider={handleSliderValue} minValue={1} maxValue={100} maxWidth='100%' disabled={volumeLoad} />
              </div>
            </div>
            <div className='content-container'>
              <div className='tab-cont'>
                <button type='button' className={`tab ${activeTab === VOICE_TAB && 'active'}`} onClick={() => onClickTab(VOICE_TAB)}>
                  <span className='material-symbols-rounded'>mic</span>
                  <b>{t('음성 방송')}</b>
                </button>
                <button type='button' className={`tab ${activeTab === AUDIO_TAB && 'active'}`} onClick={() => onClickTab(AUDIO_TAB)}>
                  <span className='material-symbols-rounded'>queue_music</span>
                  {t('음원 방송')}
                </button>
                <button type='button' className={`tab ${activeTab === TTS_TAB && 'active'}`} onClick={() => onClickTab(TTS_TAB)}>
                  <span className='material-symbols-rounded'>text_to_speech</span>
                  {t('TTS 방송')}
                </button>
              </div>

              {activeTab === VOICE_TAB && (
                <div className='tab-content'>
                  <div className='tab-head'>
                    <h4>{t('실시간 음성 방송')}</h4>
                    <p>{t('실시간 마이크 음성 방송을 진행합니다. 마이크 테스트를 통해서 마이크 입력이 정상적으로 작동하는지 확인할 수 있습니다.')}</p>
                  </div>

                  <div className='volume-test-cont'>
                    <div id='volumeMeter'>
                      {Array.from({ length: 10 }, (_, i) => i + 1).map((item: number) => (
                        <div className='volumeBar' key={item} />
                      ))}
                    </div>

                    <div className='volume-test-btns'>
                      {!isMicState ? (
                        <BtnBlue type='button' onClick={startRecording}>
                          {t('마이크 테스트')}
                        </BtnBlue>
                      ) : (
                        <BtnRed type='button' onClick={stopRecording}>
                          {t('마이크 테스트 종료')}
                        </BtnRed>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {activeTab === AUDIO_TAB && (
                <div className='tab-content'>
                  <div className='tab-head'>
                    <h4>{t('실시간 음원 방송')}</h4>
                    <p>{t('선택된 음원으로 실시간 음원 방송을 진행하는 형태입니다. 체크한 음원은 재생 순서 목록에 생기며, 순서에 맞춰서 재생되며 음원 재생이 끝나면 방송 종료됩니다.')}</p>
                  </div>

                  <div className='sub-tab-cont'>
                    <div role='presentation' className={`sub-tab ${subCurrentTab === SUB_SELECT_TAB && 'active'}`} onClick={() => setSubCurrentTab(SUB_SELECT_TAB)}>
                      {t('음원 선택')}
                    </div>
                    <div role='presentation' className={`sub-tab ${subCurrentTab === SUB_ORDER_TAB && 'active'}`} onClick={() => setSubCurrentTab(SUB_ORDER_TAB)}>
                      {t('음원 재생 순서')} {audioCheckList.length > 0 && <b>{audioCheckList.length}</b>}
                    </div>
                  </div>

                  <div className={`sub-tab-content ${subCurrentTab === SUB_SELECT_TAB && 'active'}`}>
                    <div className='audio-list-wrap'>
                      {playerListData.length > 0 ? (
                        playerListData.map((el: any, idx: number) => (
                          <label key={el.bsfCd} htmlFor={el.bsfCd} className='audio-item'>
                            <div>
                              <input type='checkbox' id={el.bsfCd} onChange={(e) => onCheckAudio(e, el)} checked={audioCheckList.some((item: any) => item.bsfCd === el.bsfCd)} />
                              <span className='audio-index'>{idx + 1}</span>
                              <div className='audio-info-cont'>
                                <div className='audio-name'>{el.bsfName}</div>
                                <span className='audio-info'>
                                  {el.bsfDuration}
                                  <b> · </b>
                                  {el.bsfFsizeMb}
                                </span>
                              </div>
                            </div>
                            <div className='play-btn'>
                              {!isLiveBroadcast ? (
                                el.bsfCd !== selectAudio.bsfCd ? (
                                  <button type='button' onClick={() => onClickPlayAudio(el)}>
                                    <span className='material-symbols-rounded'>play_arrow</span>
                                  </button>
                                ) : (
                                  <button type='button' onClick={onClickStopAudio}>
                                    <span className='material-symbols-rounded'>pause</span>
                                  </button>
                                )
                              ) : (
                                <button type='button' disabled={isLiveBroadcast}>
                                  <span className='material-symbols-rounded'>play_arrow</span>
                                </button>
                              )}
                            </div>
                          </label>
                        ))
                      ) : (
                        <IssueGuide />
                      )}
                    </div>
                  </div>

                  <div className={`sub-tab-content ${subCurrentTab === SUB_ORDER_TAB && 'active'}`}>
                    <TextInfoBox ico='lightbulb' description='드래그 앤 드롭을 지원합니다. 행 순서를 위,아래로 변경하세요' color='orange' style={{ marginBottom: '0.5rem' }} />
                    <DndProvider backend={Backend} options={{ enableMouseEvents: true }}>
                      <div className='audio-list-wrap'>
                        {audioCheckList.length > 0 ? (
                          audioCheckList.map((el: any, i) => (
                            <DraggableRow
                              type='bsfList'
                              key={el.bsfCd}
                              index={i}
                              el={el}
                              auth={auth}
                              moveRow={dragRow}
                              tableState={audioCheckList}
                              setTableState={setAudioCheckList}
                              setAudioPlayList={setAudioPlayList}
                            />
                          ))
                        ) : (
                          <IssueGuide text='음원 선택 목록에서 음원을 체크해주세요' />
                        )}
                      </div>
                    </DndProvider>
                  </div>
                </div>
              )}
              {activeTab === TTS_TAB && (
                <div className='tab-content'>
                  <div className='tab-head'>
                    <div className='flex-between'>
                      <h4>{t('실시간 TTS 방송')}</h4>
                      <div
                        className='bc-tts-btn'
                        role='button'
                        tabIndex={0}
                        onClick={() => {
                          if (isLiveBroadcast) {
                            toast.error(t('방송 중에는 불가능합니다.'));
                            return;
                          }
                          openModal.pageMoveFunction();
                          setOpenModal((prev: any) => ({ ...prev, status: false }));
                        }}
                      >
                        <b>TTS {t('음원 생성하기')}</b>
                        <span className='material-symbols-rounded'>keyboard_arrow_right</span>
                      </div>
                    </div>
                    <p>{t('각 언어에 텍스트 입력을 하고 방송시작을 누르면 각 언어에 입력된 순서를 소리로 변환하여 실시간 TTS 방송이 진행되며, TTS 방송이 끝나면 방송 종료됩니다.')}</p>
                  </div>

                  <div className='informations'>
                    <TextInfoBox ico='language' description='TTS 방송을 원하시는 언어에 해당 언어로 내용을 입력하세요.' color='orange' />
                    <BroadcastLanguage data={inputState} />
                  </div>
                  <div className='textarea-container'>
                    <AccordionTextArea name='ko' label={t('한국어')} state={inputState} setState={setInputState} maxLength={300} isExpandedState suggestionList={ttsKoList} />
                    <AccordionTextArea name='en' label={isKorean ? `English - ${t('영어')}` : 'English'} state={inputState} setState={setInputState} maxLength={300} suggestionList={ttsEnList} />
                    <AccordionTextArea name='ch' label={isKorean ? `中文 - ${t('중국어 간체')}` : `中文`} state={inputState} setState={setInputState} maxLength={300} suggestionList={ttsChList} />
                    <AccordionTextArea
                      name='vi'
                      label={isKorean ? `Tiếng Việt - ${t('베트남어')}` : `Tiếng Việt`}
                      state={inputState}
                      setState={setInputState}
                      maxLength={300}
                      suggestionList={ttsViList}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className='footer'>
            <div className='toggles'>
              <CheckChipButton //
                onClick={() => setIsStartAudioEnabled(!isStartAudioEnabled)}
                status={isStartAudioEnabled}
                text={t('방송 시작음')}
              />
              <CheckChipButton //
                onClick={() => setIsEndAudioEnabled(!isEndAudioEnabled)}
                status={isEndAudioEnabled}
                text={t('방송 종료음')}
              />
              <SwitchToggleWrapper className='red' onClick={() => onClickAlarmStop()}>
                <span className='text'>{t('알람 중지')}</span>
              </SwitchToggleWrapper>
            </div>
            <div className='buttons'>
              {!isLoading ? (
                <>
                  {/* {activeTab === AUDIO_TAB &&
                    (!isLiveBroadcast ? (
                      !isPlaying ? (
                        <BtnDeep disabled={isLiveBroadcast} onClick={() => startPlaying('preview')}>
                          {t('미리듣기')}
                        </BtnDeep>
                      ) : (
                        <BtnDeep onClick={onClickStopBtn}>{t('정지')}</BtnDeep>
                      )
                    ) : null)} */}
                  <ModalBottomButton onClick={onClickClose} text='방송 종료' negative />
                  {!isLiveBroadcast ? <ModalBottomButton onClick={onClickStartBroadcast} text='방송 시작' /> : <ModalBottomButton onClick={stopBroadcast} text='방송 중지' />}
                </>
              ) : (
                <BtnGhost>{t('연결중..')}</BtnGhost>
              )}
            </div>
          </div>
        </div>
      )}
      <Portal openModal={openSubModal?.status}>
        {openSubModal.status === true && openSubModal.type === 'delete' && <DeleteModal openModal={openSubModal} setOpenModal={setOpenSubModal} />}
        {openSubModal.status === true && openSubModal.type === 'websdk' && <WebSdkModal openModal={openSubModal} setOpenModal={setOpenSubModal} />}
      </Portal>
      <Portal openModal={micGuideState !== null}>
        {micGuideState !== null && (
          <MicPermissionModal
            permission={micGuideState}
            onClose={() => setMicGuideState(null)}
            onGranted={() => {
              setMicGuideState(null);
              toast.success(t('마이크 권한이 승인되었습니다. 방송 시작 버튼을 눌러주세요.'));
            }}
          />
        )}
      </Portal>
      {renderSdkPlugin(openModal.data)}
    </Root>
  );
};

export default BroadcastLiveModal;
